/**
 * Travel Analytics Constants
 */

// Re-export route helpers
export { TravelAnalyticsRoutes, buildAnalyticsRoute } from "../routePaths";

// ============================================================================
// Date Range Presets
// ============================================================================

export const DATE_RANGE_PRESETS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_14_DAYS: "last_14_days",
  LAST_30_DAYS: "last_30_days",
  THIS_MONTH: "this_month",
  PREVIOUS_MONTH: "previous_month",
  LAST_3_MONTHS: "last_3_months",
  YEAR_TO_DATE: "year_to_date",
  CUSTOM: "custom",
} as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[keyof typeof DATE_RANGE_PRESETS];

// ============================================================================
// Chart Colors
// ============================================================================

export const CHART_COLORS = {
  primary: "#0079D9",
  secondary: "#104977",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
  neutral: "#6b7280",

  // Product colors
  flights: "#0079D9",
  hotels: "#8b5cf6",
  cars: "#f97316",
  packages: "#06b6d4",
  activities: "#ec4899",
  transfers: "#84cc16",

  // Gradient
  gradient: ["#0079D9", "#104977"],

  // Official Ticketon categorical palette — single reusable source for ALL
  // multi-series charts. Uses ONLY brand tokens from the Tailwind preset
  // (no off-brand rainbow colors).
  palette: [
    "#0079D9", // ticketon-lighter-blue
    "#104977", // ticketon-dark-blue
    "#417EC3", // ticketon-light-blue
    "#0C5DB9", // ticketon-blue
    "#0065C0", // ticketon-lighter-blue-hover
    "#2D3647", // ticketon-dark-blue-secondary
    "#008834", // ticketon-green-paid (accent)
    "#C8B52B", // ticketon-yellow (accent)
  ],

  // Brand-aligned monochrome ramps (Ticketon style guide) — use a single
  // product family per page so distributions read cohesively instead of rainbow.
  ramps: {
    // Ticketon blues: lighter-blue → light-blue → blue → dark-blue
    flights: ["#0079D9", "#417EC3", "#0C5DB9", "#104977"],
    hotels: ["#8b5cf6", "#a78bfa", "#7c3aed", "#6d28d9"],
    cars: ["#f97316", "#fb923c", "#ea580c", "#c2410c"],
  },
} as const;

// ============================================================================
// Metric Configurations
// ============================================================================

export const METRIC_THRESHOLDS = {
  // Response time thresholds (ms)
  responseTime: {
    good: 2000,
    warning: 5000,
    critical: 10000,
  },

  // Error rate thresholds (percentage)
  errorRate: {
    good: 1,
    warning: 5,
    critical: 10,
  },

  // No result rate thresholds (percentage)
  noResultRate: {
    good: 5,
    warning: 15,
    critical: 30,
  },

  // Conversion rate thresholds (percentage)
  conversionRate: {
    poor: 1,
    average: 3,
    good: 5,
  },

  // Provider timeout rate (percentage)
  timeoutRate: {
    good: 1,
    warning: 5,
    critical: 10,
  },
} as const;

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_TABLE_PAGE_SIZES = [10, 20, 50, 100];
export const MAX_CHART_DATA_POINTS = 100;
export const DEBOUNCE_DELAY_MS = 300;
export const DEFAULT_TIMEZONE = "America/Mexico_City";
export const DEFAULT_CURRENCY = "MXN";

// ============================================================================
// Automatic-job search types
// ============================================================================

// These search types are generated by automatic/background jobs (e.g. the
// homepage combo-offers and deals widgets that fire on page load with no user
// session), not by real user searches. Callers pass this list as `skipTypes`
// to exclude them from search analytics. It is intentionally kept on the caller
// side, not hardcoded in the backend data provider.
export const AUTOMATIC_JOB_SEARCH_TYPES = ["COMBO_OFFERS", "TRAVEL_DEALS"];

// ============================================================================
// Product Configuration
// ============================================================================

export const PRODUCT_CONFIG = {
  FLIGHT: {
    labelKey: "analytics.products.flights",
    icon: "Plane",
    color: CHART_COLORS.flights,
  },
  HOTEL: {
    labelKey: "analytics.products.hotels",
    icon: "Building",
    color: CHART_COLORS.hotels,
  },
  CAR: {
    labelKey: "analytics.products.cars",
    icon: "Car",
    color: CHART_COLORS.cars,
  },
  PACKAGE: {
    labelKey: "analytics.products.packages",
    icon: "Package",
    color: CHART_COLORS.packages,
  },
  ACTIVITY: {
    labelKey: "analytics.products.activities",
    icon: "Ticket",
    color: CHART_COLORS.activities,
  },
  TRANSFER: {
    labelKey: "analytics.products.transfers",
    icon: "Bus",
    color: CHART_COLORS.transfers,
  },
} as const;

// ============================================================================
// Quality Score Weights
// ============================================================================

export const QUALITY_SCORE_WEIGHTS = {
  resultAvailability: 0.25,
  resultCount: 0.15,
  providerDiversity: 0.10,
  priceCompleteness: 0.10,
  contentCompleteness: 0.05,
  latency: 0.15,
  errorRate: 0.10,
  conversionRate: 0.10,
} as const;

// ============================================================================
// Export Formats
// ============================================================================

export const EXPORT_FORMATS = {
  CSV: "csv",
  EXCEL: "xlsx",
  JSON: "json",
} as const;

// ============================================================================
// Funnel Steps
// ============================================================================

export const FUNNEL_STEPS = [
  { key: "search_initiated", labelKey: "analytics.funnel.search_initiated" },
  { key: "search_completed", labelKey: "analytics.funnel.search_completed" },
  { key: "results_displayed", labelKey: "analytics.funnel.results_displayed" },
  { key: "result_selected", labelKey: "analytics.funnel.result_selected" },
  { key: "details_viewed", labelKey: "analytics.funnel.details_viewed" },
  { key: "checkout_started", labelKey: "analytics.funnel.checkout_started" },
  { key: "traveler_info_entered", labelKey: "analytics.funnel.traveler_info_entered" },
  { key: "payment_attempted", labelKey: "analytics.funnel.payment_attempted" },
  { key: "payment_completed", labelKey: "analytics.funnel.payment_completed" },
  { key: "booking_confirmed", labelKey: "analytics.funnel.booking_confirmed" },
] as const;
